import { useCallback, useRef } from 'react';
import type { KeyboardEvent } from 'react';

/**
 * Reusable keyboard navigation for an ARIA tablist following the WAI-ARIA Tabs Pattern.
 *
 * The layout is RTL, so the arrow keys are mirrored:
 *   - ArrowLeft  → next tab (visually next in RTL)
 *   - ArrowRight → previous tab
 *   - Home       → first tab
 *   - End        → last tab
 *
 * Implements a roving tabindex: only the active tab is focusable, and moving
 * with the keyboard both activates and focuses the target tab.
 *
 * @example
 * const { onKeyDown, registerTab } = useTabKeyboardNav(tabIds, activeTab, setActiveTab);
 * <button role="tab" ref={registerTab(tab.id)} onKeyDown={onKeyDown} tabIndex={activeTab === tab.id ? 0 : -1} />
 */
export function useTabKeyboardNav<T extends string>(
  tabIds: readonly T[],
  activeTab: T,
  setActiveTab: (id: T) => void,
) {
  const tabRefs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  const registerTab = useCallback(
    (id: T) => (element: HTMLButtonElement | null) => {
      tabRefs.current[id] = element;
    },
    [],
  );

  const focusTab = useCallback(
    (id: T) => {
      setActiveTab(id);
      // Move focus after the active tab's tabIndex updates so it is focusable.
      requestAnimationFrame(() => tabRefs.current[id]?.focus());
    },
    [setActiveTab],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = tabIds.indexOf(activeTab);
      if (currentIndex === -1) return;

      let nextIndex: number;
      switch (event.key) {
        case 'ArrowLeft': // RTL: left moves to the next tab
          nextIndex = (currentIndex + 1) % tabIds.length;
          break;
        case 'ArrowRight': // RTL: right moves to the previous tab
          nextIndex = (currentIndex - 1 + tabIds.length) % tabIds.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabIds.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      focusTab(tabIds[nextIndex]);
    },
    [activeTab, tabIds, focusTab],
  );

  return { onKeyDown, registerTab };
}

export default useTabKeyboardNav;
